import type { ApiQuery, ApiResponse, MutationResult } from '../../api/contract';
import type { ApiClient, RequestOptions } from '../../api/http';
import type { GamesRepository } from './repository';
import type { GameListQuery, GameTypeListQuery } from './types';

type GameTypeListResponse = ApiResponse<'/api/v1/game-types', 'get'>;
type GameTypeResponse = ApiResponse<'/api/v1/game-types/{id}', 'get'>;
type GameListResponse = ApiResponse<'/api/v1/games', 'get'>;
type GameResponse = ApiResponse<'/api/v1/games/{id}', 'get'>;
type PartnerGameListResponse = ApiResponse<'/api/v1/partners/{partnerId}/games', 'get'>;
type PartnerGameResponse = ApiResponse<'/api/v1/partners/{partnerId}/games/{gameId}', 'get'>;

export function createRealGamesRepository(
  apiClient: ApiClient,
  accessToken: string | null,
): GamesRepository {
  const auth: RequestOptions = accessToken === null ? {} : { accessToken };
  return {
    async listGameTypes(query, signal) {
      const result = await apiClient.get<GameTypeListResponse>('/api/v1/game-types', {
        ...auth,
        query: toGameTypeListQuery(query),
        signal,
      });
      return page(result);
    },
    async getGameType(gameTypeId, signal) {
      const result = await apiClient.get<GameTypeResponse>(gameTypePath(gameTypeId), {
        ...auth,
        signal,
      });
      return result.data.data;
    },
    async createGameType(input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/game-types', 'post'>>(
        '/api/v1/game-types',
        input,
        auth,
      );
      return mutation(result);
    },
    async updateGameType(gameTypeId, input) {
      const result = await apiClient.patch<ApiResponse<'/api/v1/game-types/{id}', 'patch'>>(
        gameTypePath(gameTypeId),
        input,
        auth,
      );
      return mutation(result);
    },
    async changeGameTypeStatus(gameTypeId, input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/game-types/{id}/status', 'post'>>(
        `${gameTypePath(gameTypeId)}/status`,
        input,
        auth,
      );
      return mutation(result);
    },
    async listGames(query, signal) {
      const result = await apiClient.get<GameListResponse>('/api/v1/games', {
        ...auth,
        query: toGameListQuery(query),
        signal,
      });
      return page(result);
    },
    async getGame(gameId, signal) {
      const result = await apiClient.get<GameResponse>(gamePath(gameId), { ...auth, signal });
      return result.data.data;
    },
    async createGame(input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/games', 'post'>>(
        '/api/v1/games',
        input,
        auth,
      );
      return mutation(result);
    },
    async updateGame(gameId, input) {
      const result = await apiClient.patch<ApiResponse<'/api/v1/games/{id}', 'patch'>>(
        gamePath(gameId),
        input,
        auth,
      );
      return mutation(result);
    },
    async changeGameStatus(gameId, input) {
      const result = await apiClient.post<ApiResponse<'/api/v1/games/{id}/status', 'post'>>(
        `${gamePath(gameId)}/status`,
        input,
        auth,
      );
      return mutation(result);
    },
    async listPartnerGames(partnerId, signal) {
      const items: PartnerGameListResponse['data'] = [];
      let pageNumber = 1;
      let totalPages = 1;
      do {
        const result = await apiClient.get<PartnerGameListResponse>(partnerGamesPath(partnerId), {
          ...auth,
          query: { page: pageNumber, pageSize: 100, sort: 'updatedAt', order: 'desc' },
          signal,
        });
        items.push(...result.data.data);
        totalPages = result.data.pagination.totalPages;
        pageNumber += 1;
      } while (pageNumber <= totalPages);
      return items;
    },
    async getPartnerGame(partnerId, gameId, signal) {
      const result = await apiClient.get<PartnerGameResponse>(partnerGamePath(partnerId, gameId), {
        ...auth,
        signal,
      });
      return result.data.data;
    },
    async createPartnerGame(partnerId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{partnerId}/games', 'post'>
      >(partnerGamesPath(partnerId), input, auth);
      return mutation(result);
    },
    async updatePartnerGame(partnerId, gameId, input) {
      const result = await apiClient.patch<PartnerGameResponse>(
        partnerGamePath(partnerId, gameId),
        input,
        auth,
      );
      return mutation(result);
    },
    async changePartnerGameStatus(partnerId, gameId, input) {
      const result = await apiClient.post<
        ApiResponse<'/api/v1/partners/{partnerId}/games/{gameId}/status', 'post'>
      >(`${partnerGamePath(partnerId, gameId)}/status`, input, auth);
      return mutation(result);
    },
  };
}

function toGameTypeListQuery(query: GameTypeListQuery): ApiQuery<'/api/v1/game-types', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...(query.filters.code.length === 0 ? {} : { code: query.filters.code }),
    ...(query.filters.name.length === 0 ? {} : { name: query.filters.name }),
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
  };
}

function toGameListQuery(query: GameListQuery): ApiQuery<'/api/v1/games', 'get'> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    sort: query.sort,
    order: query.order,
    ...(query.filters.gameTypeId.length === 0 ? {} : { gameTypeId: query.filters.gameTypeId }),
    ...(query.filters.code.length === 0 ? {} : { code: query.filters.code }),
    ...(query.filters.name.length === 0 ? {} : { name: query.filters.name }),
    ...(query.filters.status === '' ? {} : { status: query.filters.status }),
  };
}

function page<T>(result: {
  readonly data: { readonly data: readonly T[]; readonly pagination: { readonly total: number } };
  readonly requestId: string | null;
}) {
  return {
    items: result.data.data,
    total: result.data.pagination.total,
    requestId: result.requestId,
  };
}

function mutation<T>(result: {
  readonly data: { readonly data: T };
  readonly requestId: string | null;
}): MutationResult<T> {
  return { data: result.data.data, requestId: result.requestId };
}

function gameTypePath(gameTypeId: string): string {
  return `/api/v1/game-types/${encodeURIComponent(gameTypeId)}`;
}

function gamePath(gameId: string): string {
  return `/api/v1/games/${encodeURIComponent(gameId)}`;
}

function partnerGamesPath(partnerId: string): string {
  return `/api/v1/partners/${encodeURIComponent(partnerId)}/games`;
}

function partnerGamePath(partnerId: string, gameId: string): string {
  return `${partnerGamesPath(partnerId)}/${encodeURIComponent(gameId)}`;
}
